/**
 * Dashboard Page Loader
 * Pre-loads dashboard data before rendering
 */

import type { LoaderFunctionArgs } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { requireAuth, loaderCache } from './helpers';
import {
  trackRouteLoad,
  trackRouteError,
  startRouteTimer,
  calculateDataSize,
} from '@/utils/route-analytics';
import {
  kpiData,
  chartDataByPeriod,
  orderStatusData,
  activityData,
  topSourcesData,
} from '@/modules/dashboard/pages/data';
// Dashboard API service imports - Commented out until backend is ready
// import {
//   dashboardService,
//   type KpiApiResponse,
//   type ChartDataApiResponse,
//   type OrderStatusApiResponse,
//   type ActivityApiResponse,
//   type TopSourcesApiResponse,
// } from '@/services/dashboardService';

export interface DashboardData {
  kpis: Array<{
    id: string;
    title: string;
    value: string;
    change: string;
    changeType: 'positive' | 'negative';
    trend: string;
    description: string;
    icon: LucideIcon;
    color: string;
    bg: string;
    iconBg: string;
  }>;
  chartData: {
    '7d': Array<{ month: string; backlinks: number; authority: number }>;
    '30d': Array<{ month: string; backlinks: number; authority: number }>;
    '90d': Array<{ month: string; backlinks: number; authority: number }>;
    '1y': Array<{ month: string; backlinks: number; authority: number }>;
  };
  orderStatus: Array<{
    name: string;
    value: number;
    count: number;
    color: string;
    bg: string;
  }>;
  recentActivity: Array<{
    id: string;
    title: string;
    description: string;
    time: string;
    status: 'success' | 'warning' | 'info' | 'error';
    icon: LucideIcon;
    bg: string;
  }>;
  topSources: Array<{
    name: string;
    value: number;
    percentage: number;
    color: string;
    bg: string;
  }>;
}

/* ── Transform functions ── Commented out until backend API is ready
 * Uncomment these along with the API imports and fetchDashboardData API code when backend is available.
 *
 * function transformKpis(apiKpis: KpiApiResponse | null) { ... }
 * function transformChartData(apiChartData: ChartDataApiResponse | null) { ... }
 * function transformOrderStatus(apiOrderStatus: OrderStatusApiResponse | null) { ... }
 * function transformActivity(apiActivity: ActivityApiResponse | null) { ... }
 * function transformTopSources(apiTopSources: TopSourcesApiResponse | null) { ... }
 * function transformApiResponse_LEGACY(apiData: unknown): DashboardData { ... }
 *
 * See git history for full implementations.
 */

/**
 * Fetch dashboard data - USING MOCK DATA UNTIL BACKEND IS READY
 * To enable API integration, uncomment the API imports and use the commented code below
 */
async function fetchDashboardData(): Promise<DashboardData> {
  // Check cache first
  const cached = loaderCache.get<DashboardData>('dashboard');
  if (cached) {
    return cached;
  }

  // Using static mock data (no API calls)
  const dashboardData: DashboardData = {
    kpis: kpiData,
    chartData: chartDataByPeriod,
    orderStatus: orderStatusData,
    recentActivity: activityData as DashboardData['recentActivity'],
    topSources: topSourcesData,
  };

  // Cache the data for 5 minutes
  loaderCache.set('dashboard', dashboardData, 5 * 60 * 1000);

  return dashboardData;

  /* COMMENTED OUT - API Integration Code (Enable when backend is ready)

  // Fetch all components in parallel - each can fail independently
  const [apiKpis, apiChartData, apiOrderStatus, apiActivity, apiTopSources] = await Promise.all([
    dashboardService.getKpis().catch((error) => {
      console.warn('KPI API failed, using mock data:', error);
      return null;
    }),
    dashboardService.getChartData().catch((error) => {
      console.warn('Chart data API failed, using mock data:', error);
      return null;
    }),
    dashboardService.getOrderStatus().catch((error) => {
      console.warn('Order status API failed, using mock data:', error);
      return null;
    }),
    dashboardService.getActivity().catch((error) => {
      console.warn('Activity API failed, using mock data:', error);
      return null;
    }),
    dashboardService.getTopSources().catch((error) => {
      console.warn('Top sources API failed, using mock data:', error);
      return null;
    }),
  ]);

  // Transform each component (will fallback to mock if API returned null)
  const dashboardData: DashboardData = {
    kpis: transformKpis(apiKpis),
    chartData: transformChartData(apiChartData),
    orderStatus: transformOrderStatus(apiOrderStatus),
    recentActivity: transformActivity(apiActivity),
    topSources: transformTopSources(apiTopSources),
  };

  // Cache the combined data for 5 minutes
  loaderCache.set('dashboard', dashboardData, 5 * 60 * 1000);

  return dashboardData;
  */
}

/**
 * Dashboard loader function
 */
export async function dashboardLoader(args: LoaderFunctionArgs) {
  const getElapsed = startRouteTimer();

  try {
    // Check authentication
    await requireAuth(args.request);

    // Fetch dashboard data
    const data = await fetchDashboardData();

    // Track performance
    const loadTime = getElapsed();
    const dataSize = calculateDataSize(data);
    trackRouteLoad('/dashboard', loadTime, dataSize);

    // Return data directly (React Router loaders don't need to be wrapped)
    return data;
  } catch (error) {
    // If it's a redirect Response, let it propagate (don't catch it)
    if (error instanceof Response) {
      throw error;
    }

    trackRouteError('/dashboard', error as Error);

    // Return null and let the component handle the error state
    // Or throw the error to trigger error boundary
    throw error;
  }
}
